import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  Bell, 
  Search, 
  Sun, 
  Moon, 
  ChevronDown,
  Settings,
  LogOut,
  User
} from 'lucide-react';
import { Button } from '../components/ui/Button';

const Topbar = () => {
  const { user, profile, signOut } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [notifications] = useState(3);

  return (
    <header className={`
      fixed top-0 right-0 left-0 lg:left-64 z-30
      backdrop-blur-xl border-b transition-colors duration-300
      ${isDark 
        ? 'bg-gray-900/80 border-gray-800' 
        : 'bg-white/80 border-gray-200'
      }
    `}>
      <div className="px-4 lg:px-8 py-3 flex items-center justify-between">
        {/* Search - desktop */}
        <div className="hidden lg:block relative w-96">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher..."
            className={`
              w-full pl-10 pr-4 py-2 rounded-xl text-sm
              transition-colors duration-300
              ${isDark
                ? 'bg-gray-800/50 border-gray-700 text-gray-200 placeholder-gray-500 focus:bg-gray-800'
                : 'bg-gray-100 border-gray-200 text-gray-900 placeholder-gray-400 focus:bg-white'
              }
              border focus:outline-none focus:ring-2 focus:ring-primary-500/50
            `}
          />
        </div>

        {/* Right section */}
        <div className="flex items-center gap-2 ml-auto">
          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className={`
              p-2 rounded-xl transition-all duration-200
              ${isDark
                ? 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
              }
            `}
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {/* Notifications */}
          <button
            className={`
              p-2 rounded-xl transition-all duration-200 relative
              ${isDark
                ? 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
              }
            `}
          >
            <Bell size={18} />
            {notifications > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-medium">
                {notifications}
              </span>
            )}
          </button>

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className={`
                flex items-center gap-3 p-1.5 pr-3 rounded-xl
                transition-all duration-200
                ${isDark
                  ? 'hover:bg-gray-800'
                  : 'hover:bg-gray-100'
                }
              `}
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                {profile?.full_name?.[0] || user?.email?.[0]?.toUpperCase()}
              </div>
              <div className="hidden lg:block text-left">
                <p className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  {profile?.full_name || user?.email}
                </p>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {profile?.role || 'User'}
                </p>
              </div>
              <ChevronDown size={16} className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
            </button>

            {/* Dropdown menu */}
            {showUserMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowUserMenu(false)}
                />
                <div className={`
                  absolute right-0 mt-2 w-56 rounded-xl shadow-premium-lg
                  border overflow-hidden z-50 animate-fade-in
                  ${isDark
                    ? 'bg-gray-800 border-gray-700'
                    : 'bg-white border-gray-200'
                  }
                `}>
                  <div className="p-3 border-b border-gray-100 dark:border-gray-700">
                    <p className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      {profile?.full_name || user?.email}
                    </p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {profile?.role}
                    </p>
                  </div>
                  <div className="p-1">
                    <button className={`
                      w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg
                      transition-colors duration-200
                      ${isDark
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-600 hover:bg-gray-100'
                      }
                    `}>
                      <User size={16} />
                      <span>Profil</span>
                    </button>
                    <button className={`
                      w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg
                      transition-colors duration-200
                      ${isDark
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-600 hover:bg-gray-100'
                      }
                    `}>
                      <Settings size={16} />
                      <span>Paramètres</span>
                    </button>
                    <button
                      onClick={signOut}
                      className={`
                        w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg
                        transition-colors duration-200
                        ${isDark
                          ? 'text-red-400 hover:bg-red-500/10'
                          : 'text-red-600 hover:bg-red-50'
                        }
                      `}
                    >
                      <LogOut size={16} />
                      <span>Déconnexion</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Topbar;


